import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Landmark, Loader2, BookOpen } from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import DividendFilterSegmentedControl from '@/components/DividendFilterSegmentedControl';
import BuffettTestTable from '@/components/buffett/BuffettTestTable';
import BuffettTestExplainer from '@/components/buffett/BuffettTestExplainer';
import { useAuth } from '@/contexts/AuthContext';
import { useMarketTheme } from '@/hooks/useMarketTheme';
import StockModal from '@/components/StockModal';
import CompareModal from '@/components/CompareModal';
import LoginModal from '@/components/LoginModal';
import { fetchStockWithFallback } from '@/lib/stockFetch';
import { useReopenModalOnMount } from '@/lib/stockModalRestore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CANON = 'https://twylightcrow.com/buffett-test';
const SORT_ALL = ['rank', 'ticker', 'sector', 'market_cap', 'yield', 'ratio', 'roe', 'pe', 'de', 'combo'];
const SORT_NO_COMBO = SORT_ALL.filter((k) => k !== 'combo');
const SORT_NO_RATIO = SORT_ALL.filter((k) => k !== 'ratio');

function Stat({ label, value, tone = '', testId }) {
  return (
    <div className="rounded-md border border-border/60 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-mono text-lg font-bold ${tone}`}>{value == null ? '—' : Number(value).toLocaleString()}</div>
    </div>
  );
}

function useSeo() {
  useEffect(() => {
    document.title = 'Buffett $1 Retained Earnings — Stocks That Turn Kept Profits Into Market Value | TwylightCrow';
    const ogTitle = 'Warren Buffett’s $1 Retained Earnings Test, screened with ROE, P/E and Debt-to-Equity';
    const ogDesc = 'Every operating company with filed statements, tested: did each $1 of retained earnings create at least $1 of market value over the last 4–5 fiscal years? Ranked with Return on Equity, P/E and Debt-to-Equity benchmarks, with peak-cycle and accounting-anomaly cautions. Banks, REITs, MLPs, biotech and funds excluded by design.';
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('name', 'description', ogDesc);
    setMeta('name', 'keywords', 'Buffett $1 retained earnings test, one dollar test, retained earnings test stocks, return on equity screen, low debt to equity stocks, Buffett stock screener, compounders, ROE P/E debt to equity');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'TwylightCrow — DiviDrip');
    setMeta('property', 'og:url', CANON);
    setMeta('property', 'og:title', ogTitle);
    setMeta('property', 'og:description', ogDesc);
    setMeta('property', 'og:image', 'https://twylightcrow.com/dividrip-icon.png?v=3');
    setMeta('name', 'twitter:card', 'summary');
    setMeta('name', 'twitter:title', ogTitle);
    setMeta('name', 'twitter:description', ogDesc);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link); }
    link.setAttribute('href', CANON);
  }, []);
}

export default function BuffettDollarTest() {
  const { user } = useAuth();
  // PAYWALL FLIP: user?.role === 'admin' || user?.tier === 'plus'
  const isAdmin = (user?.role || '') === 'admin';
  const navigate = useNavigate();
  const { isDark } = useMarketTheme();
  const [filter, setFilter] = useState('dividend');
  const [d, setD] = useState(null);
  const [showFailers, setShowFailers] = useState(false);
  const [showThin, setShowThin] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareTicker, setCompareTicker] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  useSeo();
  useEffect(() => {
    if (!user) { setWatchlist([]); return; }
    axios.get(`${API}/portfolio`, { withCredentials: true }).then((r) => setWatchlist(r.data.map((p) => p.ticker))).catch(() => {});
  }, [user]);
  const toggleWatchlist = async (ticker) => {
    if (!user) { setLoginOpen(true); return; }
    const inList = watchlist.includes(ticker);
    try {
      if (inList) await axios.delete(`${API}/portfolio/${ticker}`, { withCredentials: true });
      else await axios.post(`${API}/portfolio`, { ticker }, { withCredentials: true });
      setWatchlist((w) => (inList ? w.filter((t) => t !== ticker) : [...w, ticker]));
    } catch { /* ignore */ }
  };
  const handleStockClick = async (ticker) => {
    setLoadingDetail(true);
    setModalOpen(true);
    try {
      const s = await fetchStockWithFallback(ticker, { timeoutMs: 3500, onFresh: (fresh) => setSelectedStock(fresh) });
      setSelectedStock(s);
    } catch { setSelectedStock(null); }
    finally { setLoadingDetail(false); }
  };
  useReopenModalOnMount(handleStockClick);
  useEffect(() => {
    let alive = true; setD(null);
    axios.get(`${API}/buffett-test/hub`, { params: { filter }, withCredentials: true }).then((r) => alive && setD(r.data)).catch(() => alive && setD({ error: true }));
    return () => { alive = false; };
  }, [filter]);
  const hideCols = filter === 'non_dividend' ? ['yield'] : [];
  const passersOnly = (d?.passers || []).filter((r) => !r.perfect_combo);
  return (
    <div className="min-h-screen bg-background" data-testid="buffett-test-page">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-0">
        <BackOrHomeButton testid="buffett-test-back-or-home-btn" showEscHint={false} />
        <div className="mt-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2"><Landmark className="w-6 h-6 text-amber-500" /> Buffett $1 Retained Earnings</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl" data-testid="buffett-test-subtitle">
              Warren Buffett’s rule for management: every $1 of profit a company keeps instead of paying out should create <strong>at least $1 of market value</strong>. We sum net income minus dividends paid over the last 4–5 fiscal years, compare the change in market cap, and pair the result with Return on Equity, P/E and Debt-to-Equity benchmarks. Scores refresh weekly.{' '}
              <Link to="/learn/buffett-one-dollar-test" className="text-primary hover:underline inline-flex items-center gap-1" data-testid="buffett-test-guide-link"><BookOpen className="w-3.5 h-3.5" /> Read the guide</Link>{' · '}
              <Link to="/accounting-anomalies" className="text-primary hover:underline inline-flex items-center gap-1" data-testid="buffett-test-anomalies-link"><BookOpen className="w-3.5 h-3.5" /> Cross-check the Accounting Anomalies hub</Link>
            </p>
          </div>
          <DividendFilterSegmentedControl value={filter} isAdmin={isAdmin} instanceId="buffett-test" onChange={setFilter} />
        </div>
        {!d && <div className="py-10 flex items-center gap-2 text-sm text-muted-foreground" data-testid="buffett-test-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading the weekly scores…</div>}
        {d?.error && <p className="py-10 text-sm text-rose-500" data-testid="buffett-test-error">Could not load the test results. Try again shortly.</p>}
        {d && !d.error && (
          <>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="buffett-test-stats">
              <Stat label="Tested" value={d.tested} testId="buffett-stat-tested" />
              <Stat label="Passed ≥ $1" value={d.passed} tone="text-emerald-600 dark:text-emerald-400" testId="buffett-stat-passed" />
              <Stat label="Compounders ≥ $2" value={d.compounders} tone="text-emerald-700 dark:text-emerald-300" testId="buffett-stat-compounders" />
              <Stat label="Perfect combo 4/4" value={d.perfect_combo} tone="text-amber-600 dark:text-amber-400" testId="buffett-stat-perfect" />
              <Stat label="Failed < $1" value={d.failed} tone="text-rose-600 dark:text-rose-400" testId="buffett-stat-failed" />
            </div>
            <div className="mt-4"><BuffettTestExplainer exclusions={d.exclusions} /></div>
            {d.tested === 0 && (
              <p className="mt-6 text-sm text-muted-foreground" data-testid="buffett-test-warming">The weekly universe run has not reached this group yet — results fill in as the cron works through the market by size.</p>
            )}
            <h2 className="mt-6 text-base md:text-lg font-bold text-amber-600 dark:text-amber-400">Perfect combo — high ROE, fair P/E, low debt, passes the $1 test</h2>
            <div className="mt-2 rounded-lg border border-amber-500/30 bg-card p-2"><BuffettTestTable rows={d.perfect} testId="buffett-table-perfect" onSelect={handleStockClick} sortable={SORT_NO_COMBO} hideCols={hideCols} /></div>
            <h2 className="mt-6 text-base md:text-lg font-bold text-emerald-600 dark:text-emerald-400">Passed the $1 test, without a perfect combo score — ranked by combo score, then dollars created</h2>
            <div className="mt-2 rounded-lg border border-border/60 bg-card p-2"><BuffettTestTable rows={passersOnly} testId="buffett-table-passers" onSelect={handleStockClick} sortable={SORT_ALL} hideCols={hideCols} /></div>
            {d.sectors?.length > 0 && (
              <div className="mt-6 rounded-lg border border-border/60 bg-card p-3" data-testid="buffett-test-sectors">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Pass rate by sector</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                  {d.sectors.map((g) => <div key={g.sector} className="flex justify-between gap-2 rounded border border-border/40 px-2 py-1"><span className="truncate">{g.sector}</span><span className="font-mono tabular-nums shrink-0">{g.pass_rate}% <span className="text-muted-foreground">({g.passed}/{g.tested})</span></span></div>)}
                </div>
              </div>
            )}
            <button type="button" onClick={() => setShowFailers((v) => !v)} className="mt-6 text-base md:text-lg font-bold text-rose-600 dark:text-rose-400 hover:underline" data-testid="buffett-test-failers-toggle">
              {showFailers ? 'Hide' : 'Show'} the failures — kept profits that shrank in the market ({d.failed})
            </button>
            {showFailers && <div className="mt-2 rounded-lg border border-border/60 bg-card p-2"><BuffettTestTable rows={d.failers} testId="buffett-table-failers" onSelect={handleStockClick} sortable={SORT_ALL} hideCols={hideCols} /></div>}
            {d.thin?.length > 0 && (
              <>
                <button type="button" onClick={() => setShowThin((v) => !v)} className="mt-6 block text-base md:text-lg font-bold text-muted-foreground hover:underline" data-testid="buffett-test-thin-toggle">
                  {showThin ? 'Hide' : 'Show'} not-meaningful results — profitable today but almost nothing retained over the window ({d.thin.length})
                </button>
                {showThin && <div className="mt-2 rounded-lg border border-border/60 bg-card p-2"><BuffettTestTable rows={d.thin} testId="buffett-table-thin" onSelect={handleStockClick} sortable={SORT_NO_RATIO} hideCols={hideCols} /></div>}
              </>
            )}
            <p className="mt-3 text-[11px] text-muted-foreground">Statements and prices from Massive. Companies with fewer than three fiscal years of filings, negative latest earnings, negative equity or under $10M revenue are not tested. As of {new Date(d.as_of).toLocaleDateString()}.</p>
          </>
        )}
        <SiteDisclaimer />
      </div>
      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedStock(null); }}
        loading={loadingDetail}
        isInWatchlist={selectedStock ? watchlist.includes(selectedStock.ticker) : false}
        onToggleWatchlist={toggleWatchlist}
        isDark={isDark}
        user={user}
        onLoginRequired={() => setLoginOpen(true)}
        onPositionsChanged={() => window.dispatchEvent(new Event('portfolio-changed'))}
        onViewPortfolio={() => { setModalOpen(false); navigate('/?view=portfolio'); }}
        onCompareClick={(t) => { if (t) { setCompareTicker(t); setCompareOpen(true); } }}
        initialTab="financial-health"
        initialChartPeriod={null}
        onSelectStock={handleStockClick}
        watchlist={watchlist}
      />
      <CompareModal open={compareOpen} onClose={() => setCompareOpen(false)} initialTickerA={compareTicker} watchlist={watchlist} onTickerClick={() => {}} />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
